/**
 * Which extension value-prop arm this visitor is in, if any.
 *
 * ── Why this is its own leaf module ─────────────────────────────────────────
 * Two callers need the answer and they must NOT share an import graph:
 *
 *   * `designOverride.ts` turns the arm into a `DESIGN_REGISTRY` entry, which
 *     pulls in every design component in the slate.
 *   * `show_bulk_download_modal.tsx` needs it to decide whether to start the
 *     PDF download up front (see below), and that file sits in INITIAL chunks
 *     on most surfaces — it is statically imported by `useDownload`,
 *     `interruption_chooser`, the work cards and the LISWP heroes.
 *
 * If the second caller reached the arm through `designOverride`, the whole
 * design library would land in those initial chunks and every page load would
 * pay for it. Measured: the design tree is ~460 KB raw / ~56 KB gzip, currently
 * confined to the async modal chunk. So this module imports the generated
 * A/B-test module and NOTHING else.
 *
 * ── Why the arm decides the download timing ────────────────────────────────
 * These arms replace the PDF-pack upsell rather than dressing it, and per
 * Richard's doc the pitch is meant to show WHILE the paper downloads ("the PDF
 * downloads in the background") rather than holding it hostage until the modal
 * is dismissed. Control's gate is untouched: it still hands the paper over on
 * close, because changing that would move the baseline the whole design slate
 * is measured against.
 *
 * ── Who decides, and who records ───────────────────────────────────────────
 * THE SERVER DECIDES. `BulkDownloadFlags.extensionVariant` carries the variant,
 * resolved in `BulkDownloadUpsellProps#bulk_download_extension_variant` through
 * `AbTest.ab_test`, which gates on `active && rollout?` — so the staged rollout
 * in Redis (`<team>:<test>:rollout`) is what controls exposure.
 *
 * This module deliberately does NOT import the generated `abTest()`. That
 * function derives the variant in the browser, where there is no rollout
 * branch, so importing it would hand out arms regardless of the dial — the
 * parity problem `AbTestFrontendRolloutChecker` exists to catch, and it catches
 * this file if the import comes back.
 *
 * THE CLIENT RECORDS, at the download click, which is the exposure event. The
 * server resolves read-only because it runs on every paper render, and a
 * bucketing there would enrol everyone who merely loaded a page. Control is
 * recorded too: it needs a denominator as much as the arms do.
 */
import { getAutoProps } from 'js/auto_props/client';

import { recordExtensionBucketing } from './recordExtensionBucketing';

const TEST_KEY =
  'demo_day_2026_bulk_download_chrome_extension_install_fake_door';

/**
 * Read the server's decision without `getAutoProps`' console noise on pages
 * that never registered these flags — which is most of them, and is not an
 * error. Mirrors `quietAutofakeFlags` and `bulkDownloadTrialPrice`.
 */
function quietExtensionVariant(): string | undefined {
  try {
    return getAutoProps('BulkDownloadFlags')?.extensionVariant || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The resolved arm name, or `null` for control, the holdout, and every visitor
 * the server did not enrol.
 */
export function extensionWaveArm(): string | null {
  const variant = quietExtensionVariant();
  if (!variant) return null;

  recordExtensionBucketing(TEST_KEY, variant);

  if (variant === 'control' || variant === 'holdout') return null;
  return variant;
}
